import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  FiUploadCloud,
  FiDownload,
  FiTrash2,
  FiEdit2,
  FiFileText,
  FiRefreshCw,
  FiX,
  FiEye,
} from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import SearchInput from '../../components/ui/SearchInput';
import DataTable from '../../components/ui/DataTable';
import ErrorBanner from '../../components/ui/ErrorBanner';
import DocumentPreviewModal from '../../../components/DocumentPreviewModal';
import { extractErrorMessage } from '../../api/client';
import {
  COMPANY_TEMPLATE_KEYS,
  getCompanyTemplates,
  uploadCompanyTemplate,
  replaceCompanyTemplate,
  deleteCompanyTemplate,
  downloadCompanyTemplate,
  downloadAllCompanyTemplatesZip,
  adminListOnboardingUsers,
  adminDownloadUserDoc,
  adminDownloadUserDocsZip,
  adminViewUserDoc,
} from '../../api/onboarding';

const STATUS_TONE = {
  PENDING: 'warning',
  DRAFT: 'info',
  SUBMITTED: 'success',
  VERIFIED: 'success',
};

const STATUS_LABEL = {
  PENDING: 'Pending',
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  VERIFIED: 'Verified',
};

const SHORT_DOC_LABELS = {
  ACKNOWLEDGEMENT_FORM: 'Acknowledgement',
  BANK_ACCOUNT_DETAILS: 'Bank Details',
  EMERGENCY_FORM: 'Emergency Form',
  UNDERTAKING: 'Undertaking',
  LEAVE_POLICIES: 'Leave Policy',
  AADHAR_COPY: 'Aadhaar',
  PAN_CARD_COPY: 'PAN Card',
};

function TemplateUploadModal({ open, onClose, templateKey, isReplace, onSuccess }) {
  const meta = COMPANY_TEMPLATE_KEYS.find((t) => t.key === templateKey) || {};
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(meta.label || '');
  const [description, setDescription] = useState(meta.desc || '');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => {
    setFile(null);
    setTitle(meta.label || '');
    setDescription(meta.desc || '');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File must be ≤ 10 MB');
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Please choose a file');
      return;
    }
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    setBusy(true);
    try {
      if (isReplace) {
        await replaceCompanyTemplate({ docKey: templateKey, title, description, file });
      } else {
        await uploadCompanyTemplate({ docKey: templateKey, title, description, file });
      }
      toast.success(isReplace ? 'Template updated' : 'Template uploaded');
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isReplace ? `Replace Template — ${meta.label || templateKey}` : `Upload Template — ${meta.label || templateKey}`}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <button
            type="button"
            disabled={busy}
            onClick={handleSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-sky-600 hover:bg-sky-700 rounded-lg transition-colors shadow-xs disabled:opacity-50"
          >
            <FiUploadCloud size={13} />
            <span>{busy ? 'Uploading...' : isReplace ? 'Replace' : 'Upload'}</span>
          </button>
        </div>
      }
    >
      <div className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 mb-1">Title *</label>
          <input
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Document title"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1">Description</label>
          <input
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief instructions or purpose"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1">File * (PDF, JPG, PNG ≤ 10 MB)</label>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('tpl-modal-file')?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed py-6 px-4 text-center transition-colors ${
              dragOver ? 'border-sky-500 bg-sky-50/50' : 'border-slate-300 bg-slate-50/50 hover:bg-sky-50/30'
            }`}
          >
            <FiUploadCloud size={20} className={dragOver ? 'text-sky-600' : 'text-slate-400'} />
            {file ? (
              <span className="font-medium text-slate-800 text-sm">{file.name}</span>
            ) : (
              <>
                <span className="text-slate-700 font-medium">Click to select file or drag &amp; drop</span>
                <span className="text-slate-400 text-[11px]">PDF, PNG, JPG up to 10 MB</span>
              </>
            )}
            <input
              id="tpl-modal-file"
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          {file && (
            <div className="mt-2 flex items-center justify-between rounded border border-sky-200 bg-sky-50/40 px-3 py-1.5">
              <span className="text-slate-700 font-mono text-xs truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-slate-400 hover:text-rose-500 ml-2"
                title="Clear file"
              >
                <FiX size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default function AdminOnboardingDocsPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState('templates');
  const [uploadModal, setUploadModal] = useState({ open: false, docKey: null, isReplace: false });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchEmp, setSearchEmp] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [downloadingTemplatesZip, setDownloadingTemplatesZip] = useState(false);
  const [downloadingUserZipId, setDownloadingUserZipId] = useState(null);
  const [selectedEmpDocs, setSelectedEmpDocs] = useState(null);

  // Document Preview Modal State
  const [previewState, setPreviewState] = useState({
    open: false,
    title: '',
    fileName: '',
    url: null,
    contentType: 'application/pdf',
    downloadFn: null,
  });

  const {
    data: templates = [],
    isLoading: loadingTemplates,
    error: tplError,
    refetch: refetchTemplates,
  } = useQuery({ queryKey: ['onboarding-templates'], queryFn: getCompanyTemplates });

  const {
    data: employees = [],
    isLoading: loadingEmployees,
    error: empError,
    refetch: refetchEmployees,
  } = useQuery({
    queryKey: ['onboarding-users'],
    queryFn: adminListOnboardingUsers,
    enabled: activeTab === 'employees',
  });

  const handleDeleteTemplate = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCompanyTemplate(deleteTarget.key);
      toast.success(`Deleted template: ${deleteTarget.label}`);
      qc.invalidateQueries({ queryKey: ['onboarding-templates'] });
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadAllTemplatesZip = async () => {
    setDownloadingTemplatesZip(true);
    try {
      await downloadAllCompanyTemplatesZip();
      toast.success('Downloaded all master templates (.zip)');
    } catch (err) {
      toast.error('Failed to download templates ZIP archive');
    } finally {
      setDownloadingTemplatesZip(false);
    }
  };

  const handleAdminDownloadUserDocsZip = async (userId, empIdentifier) => {
    setDownloadingUserZipId(userId);
    try {
      await adminDownloadUserDocsZip(userId, `${empIdentifier}_onboarding_docs.zip`);
      toast.success(`Downloaded documents for ${empIdentifier} (.zip)`);
    } catch (err) {
      toast.error('Failed to download employee documents archive');
    } finally {
      setDownloadingUserZipId(null);
    }
  };


  const handleViewEmployeeDoc = async (userId, docKey, fileName, empName) => {
    try {
      toast.loading('Loading employee document...', { id: 'admin-preview-load' });
      const { objectUrl, contentType } = await adminViewUserDoc(userId, docKey);
      toast.dismiss('admin-preview-load');
      setPreviewState({
        open: true,
        title: `${docKey.replace(/_/g, ' ')} (${empName})`,
        fileName: fileName || `${docKey}.pdf`,
        url: objectUrl,
        contentType,
        downloadFn: () => adminDownloadUserDoc(userId, docKey, fileName),
      });
    } catch (err) {
      toast.dismiss('admin-preview-load');
      toast.error('Failed to preview document');
    }
  };

  const handleClosePreview = () => {
    if (previewState.url) {
      URL.revokeObjectURL(previewState.url);
    }
    setPreviewState((prev) => ({ ...prev, open: false, url: null }));
  };

  const templateRows = useMemo(() => {
    return COMPANY_TEMPLATE_KEYS.map((meta) => {
      const uploaded = templates.find((t) => t.docKey === meta.key);
      return {
        id: meta.key,
        key: meta.key,
        label: meta.label,
        desc: meta.desc,
        uploaded: uploaded || null,
      };
    });
  }, [templates]);

  const templateColumns = [
    {
      key: 'document',
      header: 'Document Name',
      render: (r) => (
        <div className="space-y-0.5">
          <div className="font-semibold text-slate-900 flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-sky-50 text-sky-600 flex items-center justify-center shrink-0 border border-sky-100">
              <FiFileText size={13} />
            </div>
            <span>{r.label}</span>
          </div>
          <p className="text-xs text-slate-500 ml-8">{r.desc}</p>
          {r.uploaded && (
            <p className="text-[11px] text-slate-400 font-mono ml-8 truncate">
              {r.uploaded.fileName}
              {r.uploaded.fileSizeKb ? ` · ${r.uploaded.fileSizeKb} KB` : ''}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge tone={r.uploaded ? 'success' : 'neutral'}>
          {r.uploaded ? 'Configured' : 'Missing'}
        </Badge>
      ),
    },
    {
      key: 'uploadedBy',
      header: 'Last Updated',
      render: (r) => {
        if (!r.uploaded) return <span className="text-xs text-slate-400">—</span>;
        return (
          <div className="text-xs text-slate-600">
            <span>{r.uploaded.uploadedBy || 'admin'}</span>
            {r.uploaded.uploadedAt && (
              <p className="text-[11px] text-slate-400">
                {new Date(r.uploaded.uploadedAt).toLocaleDateString('en-IN')}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          {r.uploaded ? (
            <>
              <Button
                variant="outline"
                size="sm"
                icon={FiDownload}
                onClick={() => downloadCompanyTemplate(r.key, r.uploaded.fileName)}
                title="Download template"
              >
                Download
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={FiEdit2}
                onClick={() => setUploadModal({ open: true, docKey: r.key, isReplace: true })}
                title="Replace template file"
              >
                Replace
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={FiTrash2}
                onClick={() => setDeleteTarget({ key: r.key, label: r.label })}
                title="Delete template"
              />
            </>
          ) : (
            <Button
              variant="primary"
              size="sm"
              icon={FiUploadCloud}
              onClick={() => setUploadModal({ open: true, docKey: r.key, isReplace: false })}
            >
              Upload
            </Button>
          )}
        </div>
      ),
    },
  ];

  const filteredEmployees = useMemo(() => {
    return (employees || []).filter((e) => {
      if (statusFilter && e.status !== statusFilter) return false;
      if (!searchEmp.trim()) return true;
      const q = searchEmp.toLowerCase();
      return (
        (e.employeeId || '').toLowerCase().includes(q) ||
        (e.username || '').toLowerCase().includes(q) ||
        (e.firstName || '').toLowerCase().includes(q) ||
        (e.lastName || '').toLowerCase().includes(q) ||
        (e.email || '').toLowerCase().includes(q)
      );
    });
  }, [employees, searchEmp, statusFilter]);

  const employeeColumns = [
    {
      key: 'employee',
      header: 'Employee',
      render: (r) => (
        <div>
          <div className="font-semibold text-slate-900">
            {r.firstName} {r.lastName}
          </div>
          <div className="text-xs text-slate-500 font-mono">
            {r.employeeId || '—'} &nbsp;·&nbsp; @{r.username}
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (r) => <span className="text-xs text-slate-600">{r.email || '—'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge tone={STATUS_TONE[r.status] || 'neutral'}>
          {STATUS_LABEL[r.status] || r.status}
        </Badge>
      ),
    },
    {
      key: 'progress',
      header: 'Upload Progress',
      render: (r) => {
        const metadata = r.docsMetadata || {};
        const count = Object.keys(metadata).length;
        const requiredKeys = ['ACKNOWLEDGEMENT_FORM', 'BANK_ACCOUNT_DETAILS', 'EMERGENCY_FORM', 'UNDERTAKING', 'LEAVE_POLICIES', 'AADHAR_COPY', 'PAN_CARD_COPY'];
        const reqCount = requiredKeys.filter((k) => metadata[k]).length;
        const isComplete = reqCount === 7;
        return (
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-semibold text-sky-800" title={`${reqCount}/7 mandatory uploaded (${count} total)`}>
              {reqCount} / 7
            </span>
            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
              <div
                className={`h-full ${isComplete ? 'bg-emerald-500' : 'bg-sky-500'}`}
                style={{ width: `${Math.round((reqCount / 7) * 100)}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: 'submittedAt',
      header: 'Submitted Date',
      render: (r) => {
        if (!r.submittedAt) return <span className="text-xs text-slate-400">—</span>;
        return (
          <span className="text-xs text-slate-700 font-medium">
            {new Date(r.submittedAt).toLocaleDateString('en-IN')}
          </span>
        );
      },
    },
    {
      key: 'documents',
      header: 'Uploaded Files',
      align: 'right',
      render: (r) => {
        const entries = Object.entries(r.docsMetadata || {});
        if (entries.length === 0) {
          return <span className="text-xs text-slate-400">None uploaded</span>;
        }
        return (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setSelectedEmpDocs(r)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-sky-50 hover:bg-sky-100 px-3 py-1.5 text-xs font-semibold text-sky-700 border border-sky-200 transition-colors shadow-2xs cursor-pointer"
              title={`View ${entries.length} uploaded files for ${r.firstName} ${r.lastName}`}
            >
              <FiEye size={13} className="text-sky-600 shrink-0" />
              <span>View Files ({entries.length})</span>
            </button>
            <button
              type="button"
              disabled={downloadingUserZipId === r.userId}
              onClick={() =>
                handleAdminDownloadUserDocsZip(
                  r.userId,
                  r.employeeId || r.username || `emp_${r.userId}`
                )
              }
              title={`Download all ${entries.length} files as ZIP`}
              className="inline-flex items-center gap-1 rounded-lg bg-white hover:bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-700 border border-slate-200 transition-colors shadow-2xs disabled:opacity-50 cursor-pointer"
            >
              {downloadingUserZipId === r.userId ? (
                <FiRefreshCw size={12} className="animate-spin text-slate-500 shrink-0" />
              ) : (
                <FiDownload size={12} className="text-slate-500 shrink-0" />
              )}
              <span>ZIP</span>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <AppLayout>
      <div className="space-y-4">
        <PageHeader
          title="Manage Onboarding Documents"
          description="Upload company onboarding templates for employees and review submitted documents."
          actions={
            <Button
              variant="secondary"
              size="sm"
              icon={FiRefreshCw}
              onClick={() => {
                refetchTemplates();
                if (activeTab === 'employees') refetchEmployees();
              }}
            >
              Refresh
            </Button>
          }
        />

        {/* Tab switcher with light-blue active indicator */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('templates')}
            className={`pb-2.5 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'templates'
                ? 'border-sky-600 text-sky-700 font-semibold'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Master Templates ({templateRows.filter((r) => r.uploaded).length}/6)
          </button>
          <button
            onClick={() => setActiveTab('employees')}
            className={`pb-2.5 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'employees'
                ? 'border-sky-600 text-sky-700 font-semibold'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Employee Submissions ({employees.length})
          </button>
        </div>

        {/* MASTER TEMPLATES TAB */}
        {activeTab === 'templates' && (
          <div className="space-y-3">
            {tplError && <ErrorBanner error={tplError} />}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3 rounded-lg border border-slate-200">
              <div>
                <p className="text-xs font-semibold text-slate-800">Master Company Forms</p>
                <p className="text-[11px] text-slate-500">
                  Uploaded forms are served to newly hired employees for onboarding download and completion.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                icon={FiDownload}
                disabled={templateRows.filter((r) => r.uploaded).length === 0 || downloadingTemplatesZip}
                onClick={handleDownloadAllTemplatesZip}
              >
                {downloadingTemplatesZip ? 'Downloading ZIP...' : 'Download All Templates (.zip)'}
              </Button>
            </div>
            <DataTable
              columns={templateColumns}
              rows={templateRows}
              loading={loadingTemplates}
              keyField="id"
              empty="No templates defined."
            />
          </div>
        )}

        {/* EMPLOYEE SUBMISSIONS TAB */}
        {activeTab === 'employees' && (
          <div className="space-y-3">
            {empError && <ErrorBanner error={empError} />}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <SearchInput
                value={searchEmp}
                onChange={setSearchEmp}
                placeholder="Search by name, employee ID, or username..."
                className="max-w-md w-full"
              />

              <div className="flex items-center gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 focus:outline-none focus:border-sky-500"
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="DRAFT">Draft</option>
                  <option value="SUBMITTED">Submitted</option>
                </select>
              </div>
            </div>

            <DataTable
              columns={employeeColumns}
              rows={filteredEmployees}
              loading={loadingEmployees}
              keyField="userId"
              empty="No employee records found matching your filters."
            />
          </div>
        )}
      </div>

      <TemplateUploadModal
        open={uploadModal.open}
        onClose={() => setUploadModal({ open: false, docKey: null, isReplace: false })}
        templateKey={uploadModal.docKey}
        isReplace={uploadModal.isReplace}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['onboarding-templates'] })}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete ${deleteTarget?.label}?`}
        message="This will delete the master template from S3. Employees will no longer be able to download this form."
        confirmLabel="Delete Template"
        variant="danger"
        loading={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteTemplate}
      />

      {/* Clean Employee Documents Modal */}
      {selectedEmpDocs && (
        <Modal
          open={!!selectedEmpDocs}
          onClose={() => setSelectedEmpDocs(null)}
          title={`Uploaded Documents — ${selectedEmpDocs.firstName} ${selectedEmpDocs.lastName}`}
          size="lg"
        >
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-100 text-xs text-slate-500">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-700">
                  ID: {selectedEmpDocs.employeeId || selectedEmpDocs.username || `emp_${selectedEmpDocs.userId}`}
                </span>
                <span>•</span>
                <span>{selectedEmpDocs.email}</span>
              </div>
              <Badge tone={STATUS_TONE[selectedEmpDocs.status] || 'neutral'}>
                {STATUS_LABEL[selectedEmpDocs.status] || selectedEmpDocs.status || 'Pending'}
              </Badge>
            </div>

            <div className="divide-y divide-slate-100 max-h-[55vh] overflow-y-auto pr-1">
              {Object.entries(selectedEmpDocs.docsMetadata || {}).map(([docKey, docMeta]) => (
                <div key={docKey} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-50/50 px-2 rounded-lg transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0 border border-sky-100">
                      <FiFileText size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {SHORT_DOC_LABELS[docKey] || docKey.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-slate-400 font-mono truncate">
                        {docMeta?.fileName || `${docKey}.pdf`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                    <button
                      type="button"
                      onClick={() =>
                        handleViewEmployeeDoc(
                          selectedEmpDocs.userId,
                          docKey,
                          docMeta?.fileName,
                          `${selectedEmpDocs.firstName} ${selectedEmpDocs.lastName}`
                        )
                      }
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-lg transition-colors cursor-pointer"
                    >
                      <FiEye size={13} />
                      <span>Preview</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        adminDownloadUserDoc(
                          selectedEmpDocs.userId,
                          docKey,
                          docMeta?.fileName || `${docKey}.pdf`
                        )
                      }
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-colors cursor-pointer"
                    >
                      <FiDownload size={13} />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                icon={FiDownload}
                disabled={downloadingUserZipId === selectedEmpDocs.userId}
                onClick={() =>
                  handleAdminDownloadUserDocsZip(
                    selectedEmpDocs.userId,
                    selectedEmpDocs.employeeId || selectedEmpDocs.username || `emp_${selectedEmpDocs.userId}`
                  )
                }
              >
                {downloadingUserZipId === selectedEmpDocs.userId ? 'Downloading ZIP...' : 'Download All as ZIP'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedEmpDocs(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        open={previewState.open}
        onClose={handleClosePreview}
        title={previewState.title}
        fileName={previewState.fileName}
        url={previewState.url}
        contentType={previewState.contentType}
        onDownload={previewState.downloadFn}
      />
    </AppLayout>
  );
}
